/* ============================================================
   Adel — shared content source of truth.
   Both the short landing pages (teasers) and the dedicated
   deep pages import from here, so copy lives in exactly one place.
   FEE RULE: money is only ever "No registration fee". Never "free".
   ============================================================ */

import { SECTORS as ALL_SECTORS } from "./sectors";

/* Sectors that have the v2 cinematic imagery (used for the feature grids). */
export const V2_SECTOR_SLUGS = [
  "construction-infrastructure", "hospitality-catering", "healthcare-caregiving",
  "logistics-warehousing", "transportation", "manufacturing-industrial",
  "facilities-management", "security-services",
];
const V2_SET = new Set(V2_SECTOR_SLUGS);

/* Featured (v2-imaged) sectors, XL first — for landing mosaics + sector indexes. */
export const FEATURED_SECTORS = ALL_SECTORS
  .filter((s) => V2_SET.has(s.slug))
  .map((s, i) => ({ slug: s.slug, name: s.name, size: i === 0 ? "xl" : "std" }));

export const ALL_SECTORS_LIST = ALL_SECTORS;
export const SECTOR_COUNT = ALL_SECTORS.length; // 17

/* Visible "last updated" freshness signal (recency is a real AEO/AI-citation factor).
   BUMP this whenever the trust/FAQ/guide content is reviewed or updated. */
export const SITE_UPDATED = "July 2026";

/* ---------- CANDIDATE ---------- */

/* 12 steps → 3 phases, each mapped to a journey photo we already have. */
export const CANDIDATE_JOURNEY = [
  {
    id: "know", name: "We get to know you — and prepare you", image: "adel/v2/journey-consult",
    alt: "An Adel advisor sitting with a job seeker in the Nairobi office",
    steps: [
      { n: 1, t: "You reach out", b: "Call, message, or walk into our Nairobi office. There is no registration fee to begin." },
      { n: 2, t: "We get to know you", b: "We sit and listen — your skills, your hopes, and what you want for your future." },
      { n: 3, t: "We check your profile", b: "We go through your papers and experience with you, and say honestly which roles abroad fit." },
      { n: 4, t: "We match you to a role", b: "A real job with a real employer abroad, matched to what you can do and what you need." },
      { n: 5, t: "We prepare you", b: "A strong CV and real interview practice, so you walk in ready and calm." },
      { n: 6, t: "You meet the employer", b: "A real interview with the employer. We are beside you and explain anything you don't understand." },
    ],
  },
  {
    id: "journey", name: "We handle the journey", image: "adel/v2/journey-travel",
    alt: "A hopeful traveller at the airport, ready to depart",
    steps: [
      { n: 7, t: "The offer, in plain words", b: "When the offer comes, we read the contract with you — pay, hours, days off — before you sign a thing." },
      { n: 8, t: "We handle the paperwork", b: "Visa, medical, travel documents. We take care of the hard, confusing parts so you never face them alone." },
      { n: 9, t: "Before you fly", b: "A full briefing: the country, the job, your rights, and exactly who to call if you ever need help." },
      { n: 10, t: "Travel and safe arrival", b: "We arrange your journey and coordinate with the employer, who meets you the moment you land." },
    ],
  },
  {
    id: "stay", name: "We stay with you after you land", image: "adel/v2/journey-arrival",
    alt: "A settled, content worker who has started a new life abroad",
    steps: [
      { n: 11, t: "Settling in", b: "In those first weeks we stay close — helping you settle into the new country, home and team." },
      { n: 12, t: "We stay in touch", b: "Long after you start, we keep checking in. If something changes, we are still here. You are never alone." },
    ],
  },
];

export const CANDIDATE_WORRIES = [
  { worry: "Scared of being cheated or left stranded abroad?", calm: "Every role is a real job with a real employer. We explain your contract in plain words, handle your paperwork ourselves, and stay with you long after you land." },
  { worry: "Not sure the job is even real?", calm: "You interview with the employer directly, and you read and understand your contract before you agree to anything. Nothing is hidden from you." },
  { worry: "Worried about what it costs to start?", calm: "There is no registration fee. You can begin the process with us today, and we walk every step of it with you." },
];

export const CANDIDATE_PROTECT = [
  { icon: "shield", t: "Your safety comes first", b: "From the day you register to long after you arrive, your wellbeing leads every decision. We brief you on your rights and who to call." },
  { icon: "scroll", t: "Contracts you understand", b: "You read your contract in plain language — pay, hours, leave — and understand it fully before you ever sign." },
  { icon: "headset", t: "A real person, always", b: "Someone on our team knows your name and your case. Call us any time on your journey. We answer." },
  { icon: "badge", t: "No registration fee", b: "Registering with Adel carries no registration fee. You can start today." },
];

export const CANDIDATE_STORIES = [
  { img: "story-1", q: "I left Nairobi on a Tuesday and started work on a Saturday. At the end of the month, Adel called just to ask how I was doing.", a: "James O.", r: "Welder — Doha" },
  { img: "story-2", q: "When the offer came I was frightened — the visa, the paperwork, being so far from home. Adel walked me through every step.", a: "Grace K.", r: "Housekeeping — Dubai" },
  { img: "story-3", q: "I had tried other agencies before. The difference with Adel was simple. They treated me like a person, not a number.", a: "Mary W.", r: "Caregiver — Riyadh" },
];

export const CANDIDATE_START = [
  { n: "01", t: "Send your details", b: "Fill the short form, or apply on WhatsApp. There is no registration fee." },
  { n: "02", t: "We talk", b: "We get to know you and check honestly which roles abroad fit you." },
  { n: "03", t: "We line up the job", b: "We match you, prepare you, handle the paperwork — and stay with you after you land." },
];

export const CANDIDATE_STATS = [
  { v: 5000, s: "+", l: "Kenyans placed since 2019" },
  { v: 14, s: "", l: "Cities they now work in" },
  { v: 6, s: "", l: "Countries across the Gulf" },
];

/* ---------- EMPLOYER ---------- */

/* 4 simple, client-facing steps (brief → arrival), each shown as an image tile.
   The sourcing method stays private — copy and imagery sell the outcome + ease only. */
export const EMPLOYER_STEPS = [
  { n: 1, t: "Share your requirement", b: "Tell us the roles and headcount you need. We come back with salary suggestions and timelines, and confirm the salary with you.",
    image: "adel/v2/employer-step-brief", alt: "A client sharing their staffing requirement with an Adel account manager in the Nairobi office" },
  { n: 2, t: "We source quality candidates", b: "We source quality candidates and share their CVs with you for your review.",
    image: "adel/v2/employer-step-source", alt: "Confident, capable Kenyan candidates ready for roles abroad" },
  { n: 3, t: "You interview and choose", b: "We line up your chosen candidates for interview at your convenience. You choose who joins your team.",
    image: "adel/v2/employer-step-interview", alt: "An employer interviewing a candidate and choosing who joins the team" },
  { n: 4, t: "We handle documentation & travel", b: "We handle the candidate's documentation and travel arrangements end to end. Your worker arrives on time, ready to work.",
    image: "adel/v2/employer-step-travel", alt: "A passport, visa and travel documents organised by Adel ahead of departure" },
];

export const EMPLOYER_WORRIES = [
  { worry: "Worried a new hire won't last?", calm: "Every candidate is matched to your role and ready to work before you ever see them — so the people you interview are the ones worth hiring." },
  { worry: "Tired of visa rejections and paperwork delays?", calm: "We process visas, attestations and documents end to end with our own team. No serial delays, no surprises." },
  { worry: "Need people quickly, without cutting corners?", calm: "Candidates can be mobilised in as little as 14 days — quality and speed, not one at the cost of the other." },
];

export const EMPLOYER_WHY = [
  { icon: "users", t: "Quality candidates, matched to your roles", b: "Only people who fit reach your shortlist — no time wasted on the rest." },
  { icon: "wrench", t: "Ready to work from day one", b: "Skilled people who can do the job, not learn it on your site." },
  { icon: "shield", t: "Papers in order, ready to deploy", b: "Every candidate arrives ready to travel and work." },
  { icon: "filecheck", t: "Visas, attestations and contracts handled", b: "Processed in parallel, end to end. No serial delays." },
  { icon: "book", t: "Pre-departure orientation", b: "Culture, role expectations and worker rights — briefed before they fly." },
  { icon: "plane", t: "Arrival handover, on the scheduled date", b: "Our liaison sees the worker off at the airport on departure, so they arrive to you on schedule." },
  { icon: "message", t: "Regular aftercare check-ins", b: "Worker and employer, both kept in the loop after day one." },
  { icon: "refresh", t: "One accountable team", b: "One point of contact from your brief to their arrival." },
];

export const EMPLOYER_STATS = [
  { v: 5000, s: "+", l: "Placements since 2019" },
  { v: 50, s: "+", l: "Partner employers" },
  { v: 17, s: "", l: "Sectors covered" },
  { v: 14, s: "", l: "Cities served" },
];

export const EMPLOYER_CASES = [
  {
    image: "adel/v2/sector-hospitality-catering",
    tag: "Hospitality",
    quote: "We needed sixty hospitality staff within nine weeks. Adel ran the entire chain — interviews, medicals, visas and flights — and every one of them cleared and arrived on schedule.",
    stats: [{ v: "60", l: "staff placed" }, { v: "9 wks", l: "brief to arrival" }, { v: "0", l: "visa rejections" }],
    by: "Head of HR", org: "Five-star hotel group · Doha",
  },
];

/* ---------- FAQ (answer-first; feeds the /faq page + FAQPage JSON-LD) ---------- */
export const FAQ_ITEMS = [
  { audience: "candidate", q: "Does Adel Group charge jobseekers a registration fee?",
    a: "No — there is no registration fee to begin. You can start your process with us at no upfront cost. If any payment is needed at all, it only ever comes after you have received your official offer letter — never before it. We walk every step with you and explain any cost clearly, so you can begin today with confidence." },
  { audience: "candidate", q: "Is Adel Group a licensed, legitimate recruitment agency?",
    a: "Yes. Adel Group (Adel Abdelrahman Group Limited) is licensed by Kenya's National Employment Authority — licence RA/2025/09/56 — and is a listed member of ASMAK." },
  { audience: "candidate", q: "How do I get a job in the Gulf from Kenya?",
    a: "Reach out and register with us or on WhatsApp. We get to know you, match you to a real role with a real employer abroad, prepare you for the interview, and once you are offered the job we handle your documentation and travel end to end.",
    inlineLink: { text: "register with us", to: "/candidates/apply" } },
  { audience: "candidate", q: "Which countries does Adel Group place workers in?",
    a: "We place Kenyan workers across the Gulf — Saudi Arabia, the United Arab Emirates, Qatar, Oman, Bahrain and Kuwait." },
  { audience: "candidate", q: "What kinds of jobs and sectors are available?",
    a: "Across 17 sectors — including construction, hospitality and catering, healthcare and caregiving, logistics and warehousing, security, facilities management and manufacturing — for skilled and semi-skilled roles." },
  { audience: "candidate", q: "How long does it take to be placed and travel?",
    a: "It depends on the role, the country and your documents, but candidates can be mobilised in as little as 14 days once everything is in order." },
  { audience: "candidate", q: "How do I know the job and contract are real?",
    a: "You interview with the employer directly, and you read and understand your contract — pay, hours, days off — in plain language before you agree to anything. Nothing is hidden from you." },
  { audience: "candidate", q: "Is it safe to work in the Gulf, and what support do I get?",
    a: "Your safety leads every decision. Before you fly we brief you on the country, your job and your rights, and who to call for help. Someone meets you when you land, and we keep checking in long after you start." },
  { audience: "candidate", q: "How do I apply?",
    a: "Submit your CV on our apply page or send it on WhatsApp. A real person reads every application and replies. There is no registration fee to start." },

  { audience: "employer", q: "How does Adel Group work for employers?",
    a: "You share your requirement — the roles and headcount you need. We come back with salary suggestions and timelines, source quality candidates and share their CVs, line up interviews at your convenience, and once you choose we handle the documentation and travel end to end." },
  { audience: "employer", q: "How quickly can you mobilise workers?",
    a: "In as little as 14 days, depending on the roles, the destination country and the documentation involved." },
  { audience: "employer", q: "Which sectors does Adel Group recruit for?",
    a: "17 sectors and more — including construction and infrastructure, hospitality, healthcare and caregiving, logistics, security, facilities management and manufacturing — staffed with skilled workers from Kenya. And if your requirement isn't on the list, tell us anyway: we take on any role you need." },
  { audience: "employer", q: "Is Adel Group licensed and compliant?",
    a: "Yes. We are licensed by Kenya's National Employment Authority (RA/2025/09/56) and are a listed ASMAK member, and we process visas, attestations and documents end to end with our own team." },
  { audience: "employer", q: "How do we get started as an employer?",
    a: "Send us your brief — the roles and headcount you need — together with your demand letter. We reply with realistic salary bands and lead times, then begin sourcing candidates for you to review and choose." },
];

/* ---------- CONTACT ---------- */
export const PHONE = "+254 117 222 777";
export const PHONE_TEL = "+254117222777";
export const WHATSAPP = "254729447777"; // wa.me number
export const WHATSAPP_MSG = "Hello Adel, I want to apply for a job abroad. My name is ";
